// Grid Impact API client — public endpoints (no auth required)

import { API } from "./api";

// ─── Types ───────────────────────────────────────────────────────────

export interface GridRegionImpact {
  ba_code: string;
  timestamp: string;
  total_generation_mwh: number;
  climate_change_kg_co2eq: number;
  carbon_intensity_kg_per_mwh: number;
  human_health_dalys: number | null;
  ecosystem_quality_species_yr: number | null;
  resource_scarcity_usd2013: number | null;
  // Midpoint categories
  ozone_depletion_kg_cfc11eq: number | null;
  terrestrial_acidification_kg_so2eq: number | null;
  freshwater_eutrophication_kg_peq: number | null;
  marine_eutrophication_kg_neq: number | null;
  human_toxicity_kg_14dceq: number | null;
  photochemical_oxidant_kg_nmvoceq: number | null;
  particulate_matter_kg_pm10eq: number | null;
  terrestrial_ecotoxicity_kg_14dceq: number | null;
  freshwater_ecotoxicity_kg_14dceq: number | null;
  marine_ecotoxicity_kg_14dceq: number | null;
  ionising_radiation_kbq_u235eq: number | null;
  agricultural_land_m2a: number | null;
  urban_land_m2a: number | null;
  natural_land_m2: number | null;
  water_depletion_m3: number | null;
  metal_depletion_kg_feeq: number | null;
  fossil_depletion_kg_oileq: number | null;
}

export interface GridSnapshotResponse {
  hour: string;
  regions: GridRegionImpact[];
  count: number;
  available_hours: { start: string; end: string };
}

export interface GridCurrentResponse {
  ba_code: string;
  current_hour: string;
  impact: GridRegionImpact | null;
  data_age_minutes: number;
  message: string | null;
}

export interface GridHistoryResponse {
  ba_code: string;
  start_time: string;
  end_time: string;
  impacts: GridRegionImpact[];
  count: number;
}

export interface FuelMix {
  type: string;
  label: string;
  generation_mwh: number;
  color: string;
}

export interface GenerationMixResponse {
  ba_code: string;
  hour: string;
  fuels: FuelMix[];
  total_generation_mwh: number;
}

export interface AvailableRegionsResponse {
  regions: string[];
  count: number;
  data_start: string | null;
  data_end: string | null;
}

// Scale mode for metric display
export type MetricScale = "intensity" | "annual";

// Metric keys that can be displayed on the map
export type GridMetricKey =
  | "carbon_intensity_kg_per_mwh"
  | "human_health_dalys"
  | "ecosystem_quality_species_yr"
  | "resource_scarcity_usd2013"
  | "ozone_depletion_kg_cfc11eq"
  | "terrestrial_acidification_kg_so2eq"
  | "freshwater_eutrophication_kg_peq"
  | "marine_eutrophication_kg_neq"
  | "human_toxicity_kg_14dceq"
  | "photochemical_oxidant_kg_nmvoceq"
  | "particulate_matter_kg_pm10eq"
  | "terrestrial_ecotoxicity_kg_14dceq"
  | "freshwater_ecotoxicity_kg_14dceq"
  | "marine_ecotoxicity_kg_14dceq"
  | "ionising_radiation_kbq_u235eq"
  | "agricultural_land_m2a"
  | "urban_land_m2a"
  | "natural_land_m2"
  | "water_depletion_m3"
  | "metal_depletion_kg_feeq"
  | "fossil_depletion_kg_oileq";

export const METRIC_LABELS: Record<GridMetricKey, string> = {
  carbon_intensity_kg_per_mwh: "Carbon Intensity",
  human_health_dalys: "Human Health",
  ecosystem_quality_species_yr: "Ecosystem Quality",
  resource_scarcity_usd2013: "Resource Scarcity",
  ozone_depletion_kg_cfc11eq: "Ozone Depletion",
  terrestrial_acidification_kg_so2eq: "Terrestrial Acidification",
  freshwater_eutrophication_kg_peq: "Freshwater Eutrophication",
  marine_eutrophication_kg_neq: "Marine Eutrophication",
  human_toxicity_kg_14dceq: "Human Toxicity",
  photochemical_oxidant_kg_nmvoceq: "Photochemical Oxidant",
  particulate_matter_kg_pm10eq: "Particulate Matter",
  terrestrial_ecotoxicity_kg_14dceq: "Terrestrial Ecotoxicity",
  freshwater_ecotoxicity_kg_14dceq: "Freshwater Ecotoxicity",
  marine_ecotoxicity_kg_14dceq: "Marine Ecotoxicity",
  ionising_radiation_kbq_u235eq: "Ionising Radiation",
  agricultural_land_m2a: "Agricultural Land",
  urban_land_m2a: "Urban Land",
  natural_land_m2: "Natural Land Transformation",
  water_depletion_m3: "Water Depletion",
  metal_depletion_kg_feeq: "Metal Depletion",
  fossil_depletion_kg_oileq: "Fossil Depletion",
};

export const METRIC_UNITS: Record<GridMetricKey, string> = {
  carbon_intensity_kg_per_mwh: "kg CO\u2082-eq/MWh",
  human_health_dalys: "DALYs/MWh",
  ecosystem_quality_species_yr: "species.yr/MWh",
  resource_scarcity_usd2013: "USD 2013/MWh",
  ozone_depletion_kg_cfc11eq: "kg CFC-11-eq/MWh",
  terrestrial_acidification_kg_so2eq: "kg SO\u2082-eq/MWh",
  freshwater_eutrophication_kg_peq: "kg P-eq/MWh",
  marine_eutrophication_kg_neq: "kg N-eq/MWh",
  human_toxicity_kg_14dceq: "kg 1,4-DCB-eq/MWh",
  photochemical_oxidant_kg_nmvoceq: "kg NMVOC-eq/MWh",
  particulate_matter_kg_pm10eq: "kg PM10-eq/MWh",
  terrestrial_ecotoxicity_kg_14dceq: "kg 1,4-DCB-eq/MWh",
  freshwater_ecotoxicity_kg_14dceq: "kg 1,4-DCB-eq/MWh",
  marine_ecotoxicity_kg_14dceq: "kg 1,4-DCB-eq/MWh",
  ionising_radiation_kbq_u235eq: "kBq U235-eq/MWh",
  agricultural_land_m2a: "m\u00B2a/MWh",
  urban_land_m2a: "m\u00B2a/MWh",
  natural_land_m2: "m\u00B2/MWh",
  water_depletion_m3: "m\u00B3/MWh",
  metal_depletion_kg_feeq: "kg Fe-eq/MWh",
  fossil_depletion_kg_oileq: "kg oil-eq/MWh",
};

// Annualized units (rate × total_generation_mwh)
export const ANNUAL_METRIC_UNITS: Record<GridMetricKey, string> = {
  carbon_intensity_kg_per_mwh: "kg CO\u2082-eq/yr",
  human_health_dalys: "DALYs/yr",
  ecosystem_quality_species_yr: "species.yr/yr",
  resource_scarcity_usd2013: "USD 2013/yr",
  ozone_depletion_kg_cfc11eq: "kg CFC-11-eq/yr",
  terrestrial_acidification_kg_so2eq: "kg SO\u2082-eq/yr",
  freshwater_eutrophication_kg_peq: "kg P-eq/yr",
  marine_eutrophication_kg_neq: "kg N-eq/yr",
  human_toxicity_kg_14dceq: "kg 1,4-DCB-eq/yr",
  photochemical_oxidant_kg_nmvoceq: "kg NMVOC-eq/yr",
  particulate_matter_kg_pm10eq: "kg PM10-eq/yr",
  terrestrial_ecotoxicity_kg_14dceq: "kg 1,4-DCB-eq/yr",
  freshwater_ecotoxicity_kg_14dceq: "kg 1,4-DCB-eq/yr",
  marine_ecotoxicity_kg_14dceq: "kg 1,4-DCB-eq/yr",
  ionising_radiation_kbq_u235eq: "kBq U235-eq/yr",
  agricultural_land_m2a: "m\u00B2a/yr",
  urban_land_m2a: "m\u00B2a/yr",
  natural_land_m2: "m\u00B2/yr",
  water_depletion_m3: "m\u00B3/yr",
  metal_depletion_kg_feeq: "kg Fe-eq/yr",
  fossil_depletion_kg_oileq: "kg oil-eq/yr",
};

// Endpoint metrics (primary, shown as quick-pick buttons)
export const ENDPOINT_METRICS: { key: GridMetricKey; label: string }[] = [
  { key: "carbon_intensity_kg_per_mwh", label: "Carbon Intensity" },
  { key: "human_health_dalys", label: "Human Health" },
  { key: "ecosystem_quality_species_yr", label: "Ecosystem Quality" },
  { key: "resource_scarcity_usd2013", label: "Resource Scarcity" },
];

// All midpoint metrics (shown in expandable dropdown)
export const MIDPOINT_METRICS: { key: GridMetricKey; label: string; unit: string }[] = [
  { key: "ozone_depletion_kg_cfc11eq", label: "Ozone Depletion", unit: "kg CFC-11-eq/MWh" },
  { key: "terrestrial_acidification_kg_so2eq", label: "Terrestrial Acidification", unit: "kg SO\u2082-eq/MWh" },
  { key: "freshwater_eutrophication_kg_peq", label: "Freshwater Eutrophication", unit: "kg P-eq/MWh" },
  { key: "marine_eutrophication_kg_neq", label: "Marine Eutrophication", unit: "kg N-eq/MWh" },
  { key: "human_toxicity_kg_14dceq", label: "Human Toxicity", unit: "kg 1,4-DCB-eq/MWh" },
  { key: "photochemical_oxidant_kg_nmvoceq", label: "Photochemical Oxidant", unit: "kg NMVOC-eq/MWh" },
  { key: "particulate_matter_kg_pm10eq", label: "Particulate Matter", unit: "kg PM10-eq/MWh" },
  { key: "terrestrial_ecotoxicity_kg_14dceq", label: "Terrestrial Ecotoxicity", unit: "kg 1,4-DCB-eq/MWh" },
  { key: "freshwater_ecotoxicity_kg_14dceq", label: "Freshwater Ecotoxicity", unit: "kg 1,4-DCB-eq/MWh" },
  { key: "marine_ecotoxicity_kg_14dceq", label: "Marine Ecotoxicity", unit: "kg 1,4-DCB-eq/MWh" },
  { key: "ionising_radiation_kbq_u235eq", label: "Ionising Radiation", unit: "kBq U235-eq/MWh" },
  { key: "agricultural_land_m2a", label: "Agricultural Land", unit: "m\u00B2a/MWh" },
  { key: "urban_land_m2a", label: "Urban Land", unit: "m\u00B2a/MWh" },
  { key: "natural_land_m2", label: "Natural Land Transformation", unit: "m\u00B2/MWh" },
  { key: "water_depletion_m3", label: "Water Depletion", unit: "m\u00B3/MWh" },
  { key: "metal_depletion_kg_feeq", label: "Metal Depletion", unit: "kg Fe-eq/MWh" },
  { key: "fossil_depletion_kg_oileq", label: "Fossil Depletion", unit: "kg oil-eq/MWh" },
];

// ─── API Functions ───────────────────────────────────────────────────

export async function getGridSnapshot(hour?: string): Promise<GridSnapshotResponse> {
  const params = hour ? `?hour=${encodeURIComponent(hour)}` : "";
  const res = await fetch(`${API}/grid/snapshot${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getGridDetail(baCode: string, hour?: string): Promise<GridRegionImpact> {
  const params = hour ? `?hour=${encodeURIComponent(hour)}` : "";
  const res = await fetch(`${API}/grid/detail/${encodeURIComponent(baCode)}${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getGridHistory(
  baCode: string,
  start?: string,
  end?: string,
): Promise<GridHistoryResponse> {
  const params = new URLSearchParams();
  if (start) params.set("start", start);
  if (end) params.set("end", end);
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await fetch(`${API}/grid/history/${encodeURIComponent(baCode)}${qs}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getGenerationMix(
  baCode: string,
  hour?: string,
  period?: string,
): Promise<GenerationMixResponse> {
  const searchParams = new URLSearchParams();
  if (hour) searchParams.set("hour", hour);
  if (period) searchParams.set("period", period);
  const qs = searchParams.toString() ? `?${searchParams}` : "";
  const res = await fetch(`${API}/grid/generation-mix/${encodeURIComponent(baCode)}${qs}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getAvailableRegions(): Promise<AvailableRegionsResponse> {
  const res = await fetch(`${API}/grid/regions`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}
